<template>
  <base-modal
    class="modal-teacher"
    title="Edit Teaching Offerings"
    @close="$emit('close')"
    data-test-id="edit-teaching-offerings-modal"
  >
    <form class="modal-teacher__form" @submit.prevent="save" data-test-id="edit-teaching-offerings-form">
      <form-control label="TEACHING OFFERINGS" theme="dark" data-test-id="teaching-offerings-section">
        <div
          v-for="(offering, index) in model.offerings"
          :key="offering.code"
          class="modal-teacher__columns"
          data-test-id="teaching-offering-item"
        >
          <div class="modal-teacher__column">
            <label :for="`teaching-offering-${index}`">{{ offering.label }}</label>
          </div>
          <div class="modal-teacher__column">
            <voxy-select
              :id="`teaching-offering-${index}`"
              v-model="offering.value"
              :options="offeredOptions"
              placeholder="-"
              theme="ghost"
              :data-test-id="`teaching-offering-select-${offering.code}`"
            ></voxy-select>
          </div>
        </div>
      </form-control>

      <div class="modal-teacher__submit">
        <p v-if="errorMessage" data-test-id="error-message">{{ errorMessage }}</p>
        <voxy-button
          view="primary"
          type="submit"
          size="s"
          :disabled="!hasChanges || isSaving"
          data-test-id="submit-button"
        >
          {{ isSaving ? 'Saving...' : 'Save' }}
        </voxy-button>
      </div>
    </form>
  </base-modal>
</template>

<script>
import { VoxyButton, VoxySelect } from '@voxy-team/vue-voxy';
import { mapActions } from 'vuex';

import BaseModal from '@/components/BaseModal';
import FormControl from '@/components/form/FormControl';

const OFFERED = 'Yes';
const NOT_OFFERED = 'No';
const offeredOptions = [OFFERED, NOT_OFFERED];
const DEFAULT_ERROR_MESSAGE = 'Something went wrong while saving. Please try again.';

// null keeps the dropdown on its placeholder: an unknown designation (webackend unreachable, tutor
// without a webackend user) is not shown as a No.
const offeredLabel = offered => {
  if (offered === true) {
    return OFFERED;
  }
  if (offered === false) {
    return NOT_OFFERED;
  }
  return null;
};

export default {
  name: 'ModalTeacherTeachingOfferings',

  components: {
    BaseModal,
    FormControl,
    VoxyButton,
    VoxySelect,
  },

  props: {
    teacherProfile: {
      default: () => ({}),
      required: true,
      type: Object,
    },
  },

  data() {
    const offerings = (this.teacherProfile.teaching_offerings || []).map(offering => ({
      code: offering.code,
      label: offering.label,
      value: offeredLabel(offering.offered),
      initialValue: offeredLabel(offering.offered),
    }));

    return {
      offeredOptions,
      model: { offerings },
      isSaving: false,
      errorMessage: '',
    };
  },

  computed: {
    changedOfferings() {
      return this.model.offerings.filter(
        offering => offering.value !== null && offering.value !== offering.initialValue,
      );
    },

    hasChanges() {
      return this.changedOfferings.length > 0;
    },
  },

  methods: {
    ...mapActions('manageTeachers', {
      updateTeachingOffering: 'UPDATE_TEACHING_OFFERING',
    }),

    async save() {
      this.errorMessage = '';
      this.isSaving = true;

      try {
        // Sequential on purpose: the manage-teachers api aborts its shared AbortController on every
        // call, so parallel saves would cancel each other.
        await this.changedOfferings.reduce(
          (previousSave, offering) =>
            previousSave.then(() =>
              this.updateTeachingOffering({
                teacherHashEmail: this.$route.params.teacherHashEmail,
                offeringCode: offering.code,
                offered: offering.value === OFFERED,
              }),
            ),
          Promise.resolve(),
        );
        this.$emit('close');
      } catch (error) {
        this.errorMessage = error && error.detail ? error.detail : DEFAULT_ERROR_MESSAGE;
      } finally {
        this.isSaving = false;
      }
    },
  },
};
</script>

<style lang="scss" scoped>
@import '~styles/components/teacher-management-sections';
</style>
